import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MessageSquare, Video, Phone } from 'lucide-react';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    setLoading(true);
    try {
      await register({ name, email, password, phone });
      toast.success('Registration successful! Please login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MessageSquare className="w-10 h-10 text-[#4338CA]" />
            <Video className="w-10 h-10 text-[#4338CA]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2 font-heading">CallChat</h1>
          <p className="text-[#475569]">Connect, Chat, and Collaborate</p>
        </div>

        <div className="bg-white rounded-md border border-[#E2E8F0] p-8">
          <h2 className="text-2xl font-bold mb-6 text-[#0F172A] font-heading">Create Account</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" data-testid="register-name-label">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone" data-testid="register-phone-label">Phone Number</Label>
              <div className="flex items-center gap-2 mt-1">
                <Phone className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+919876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  data-testid="register-phone-input"
                />
              </div>
              <p className="text-xs text-[#94A3B8] mt-1">Used for contact discovery (like WhatsApp)</p>
            </div>
            <div>
              <Label htmlFor="email" data-testid="register-email-label">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" data-testid="register-password-label">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-md"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          <p className="mt-6 text-center text-[#475569]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#4338CA] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
